import { type VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import type { CommonProps } from '../../types';
import { ButtonOrLink } from '../button-or-link';
import type { VenueId } from '@beat-venues/common-types';

import type { JSX } from 'react';

export interface LogoProps
    extends CommonProps,
        VariantProps<typeof LogoStyles> {
    venueId: VenueId | 'vip' | 'my-beat' | 'ticket-master' | 'nmbs' | 'mivb';
    isInternalLink?: boolean;
    href?: string | undefined;
    invert?: boolean;
    overwriteScreenreaderText?: string;
}

export const LogoStyles = cva('focus', {
    variants: {
        size: {
            xs: 'w-8',
            sm: 'w-12',
            md: 'w-auto',
        },
        color: {
            white: 'fill-white',
        },
    },
    defaultVariants: {
        size: 'md',
    },
});

export const Logo = ({
    size,
    color,
    href,
    className,
    venueId,
    invert = false,
    testId = 'logo',
    isInternalLink,
    overwriteScreenreaderText,
    ...props
}: LogoProps): JSX.Element => {
    const { t } = useTranslation();
    const alt = t(`venues.${venueId}.name`);
    let src: string;
    if (invert) {
        src = `/assets/${venueId}/logo/logo_invert.svg`;
    } else {
        if (size === 'sm') {
            src = `/assets/${venueId}/logo/logo_small.svg`;
        } else {
            src = `/assets/${venueId}/logo/logo.svg`;
        }
    }

    const image = (imageTestId?: string): JSX.Element => (
        <img
            alt={alt}
            className={twMerge(LogoStyles({ className, size, color }))}
            data-testid={imageTestId}
            src={src}
            {...props}
        />
    );

    if (href) {
        return (
            <ButtonOrLink
                className="focus z-10 inline-block h-fit px-2 pb-2"
                href={href}
                testId={testId}
                {...(isInternalLink ? { target: '_self' } : {})}
            >
                {image()}
                <span className="sr-only">
                    {overwriteScreenreaderText ??
                        t('goToWebsite', {
                            brand: t(`venues.${venueId}.name`),
                        })}
                </span>
            </ButtonOrLink>
        );
    }

    return image(testId);
};
